import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface LocationReminder {
  id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  radius_meters: number;
  trigger_on: 'arrival' | 'departure';
  reminder_text: string;
  is_active: boolean;
  created_at: string;
}

interface SavedLocation {
  id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

const LocationReminders: React.FC = () => {
  const [reminders, setReminders] = useState<LocationReminder[]>([]);
  const [savedLocations, setSavedLocations] = useState<SavedLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationPermission, setLocationPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');

  // Form states
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [customAddress, setCustomAddress] = useState('');
  const [customLat, setCustomLat] = useState('');
  const [customLng, setCustomLng] = useState('');
  const [reminderText, setReminderText] = useState('');
  const [triggerOn, setTriggerOn] = useState<'arrival' | 'departure'>('arrival');
  const [radiusMeters, setRadiusMeters] = useState('200');

  // New location form
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationAddress, setNewLocationAddress] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [remindersRes, locationsRes] = await Promise.all([
        apiService.getLocationReminders(),
        apiService.getSavedLocations()
      ]);
      setReminders(remindersRes.reminders || []);
      setSavedLocations(locationsRes.locations || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    checkLocationPermission();
  }, [fetchData]);

  const checkLocationPermission = async () => {
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setLocationPermission(result.state as 'granted' | 'denied' | 'prompt');
        result.onchange = () => setLocationPermission(result.state as 'granted' | 'denied' | 'prompt');
      } catch (e) {
        console.log('Permission API not supported');
      }
    }
  };

  const getCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setCustomLat(position.coords.latitude.toString());
          setCustomLng(position.coords.longitude.toString());
        },
        (error) => {
          console.error('Location error:', error);
          alert('Could not get your location. Please enable location services.');
        }
      );
    }
  };

  const handleAddReminder = async () => {
    if (!reminderText.trim()) {
      alert('Please enter reminder text');
      return;
    }

    let lat, lng, address;
    if (selectedLocation) {
      const loc = savedLocations.find(l => l.id === selectedLocation);
      if (loc) {
        lat = loc.latitude;
        lng = loc.longitude;
        address = loc.address;
      }
    } else {
      lat = parseFloat(customLat);
      lng = parseFloat(customLng);
      address = customAddress;
    }

    if (!lat || !lng) {
      alert('Please select a location or enter coordinates');
      return;
    }

    try {
      await apiService.createLocationReminder({
        name: address || 'Custom Location',
        address: address || `${lat}, ${lng}`,
        latitude: lat,
        longitude: lng,
        radiusMeters: parseInt(radiusMeters),
        triggerOn,
        reminderText
      });
      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to create reminder:', error);
      alert('Failed to create reminder');
    }
  };

  const handleAddLocation = async () => {
    if (!newLocationName.trim() || !newLocationAddress.trim()) {
      alert('Please enter name and address');
      return;
    }

    if (!currentLocation) {
      alert('Please get your current location first');
      return;
    }

    try {
      await apiService.createSavedLocation({
        name: newLocationName,
        address: newLocationAddress,
        latitude: currentLocation.lat,
        longitude: currentLocation.lng
      });
      setShowAddLocationModal(false);
      setNewLocationName('');
      setNewLocationAddress('');
      fetchData();
    } catch (error) {
      console.error('Failed to save location:', error);
      alert('Failed to save location');
    }
  };

  const handleToggleReminder = async (id: string, isActive: boolean) => {
    try {
      await apiService.updateLocationReminder(id, { isActive: !isActive });
      setReminders(reminders.map(r => r.id === id ? { ...r, is_active: !isActive } : r));
    } catch (error) {
      console.error('Failed to update reminder:', error);
    }
  };

  const handleDeleteReminder = async (id: string) => {
    if (!confirm('Delete this reminder?')) return;
    try {
      await apiService.deleteLocationReminder(id);
      setReminders(reminders.filter(r => r.id !== id));
    } catch (error) {
      console.error('Failed to delete reminder:', error);
    }
  };

  const handleDeleteLocation = async (id: string) => {
    if (!confirm('Delete this saved location?')) return;
    try {
      await apiService.deleteSavedLocation(id);
      setSavedLocations(savedLocations.filter(l => l.id !== id));
    } catch (error) {
      console.error('Failed to delete location:', error);
    }
  };

  const resetForm = () => {
    setSelectedLocation('');
    setCustomAddress('');
    setCustomLat('');
    setCustomLng('');
    setReminderText('');
    setTriggerOn('arrival');
    setRadiusMeters('200');
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '16px' }}>📍</div>
        <p>Loading location reminders...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }} data-testid="location-reminders">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>📍 Location Reminders</h1>
          <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '4px 0 0' }}>
            Get reminded when you arrive or leave specific places
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setShowAddLocationModal(true)}
            style={{
              padding: '10px 16px',
              backgroundColor: 'white',
              color: '#667eea',
              border: '2px solid #667eea',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            + Save Location
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              padding: '10px 16px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            + Add Reminder
          </button>
        </div>
      </div>

      {/* Location Permission Banner */}
      {locationPermission === 'denied' && (
        <div style={{
          padding: '16px',
          backgroundColor: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '12px',
          marginBottom: '20px'
        }}>
          <strong>⚠️ Location Access Required</strong>
          <p style={{ margin: '8px 0 0', fontSize: '0.9rem' }}>
            Please enable location permissions in your browser settings to use geofence reminders.
          </p>
        </div>
      )}

      {/* Saved Locations */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>
          🏠 Saved Locations ({savedLocations.length})
        </h3>
        {savedLocations.length === 0 ? (
          <p style={{ color: '#9ca3af', textAlign: 'center', padding: '20px' }}>
            No saved locations. Add places you frequently visit like Home, Work, Gym, etc.
          </p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {savedLocations.map(loc => (
              <div
                key={loc.id}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{loc.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{loc.address}</div>
                </div>
                <button
                  onClick={() => handleDeleteLocation(loc.id)}
                  style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Active Reminders */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
        padding: '20px'
      }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>
          🔔 Reminders ({reminders.length})
        </h3>
        {reminders.length === 0 ? (
          <p style={{ color: '#9ca3af', textAlign: 'center', padding: '40px' }}>
            No location reminders set up yet. Create one to get notified when you arrive or leave a place.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {reminders.map(reminder => (
              <div
                key={reminder.id}
                style={{
                  padding: '16px',
                  backgroundColor: reminder.is_active ? '#f0fdf4' : '#f9fafb',
                  borderRadius: '12px',
                  border: `2px solid ${reminder.is_active ? '#22c55e' : '#e5e7eb'}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{
                      padding: '2px 8px',
                      backgroundColor: reminder.trigger_on === 'arrival' ? '#dbeafe' : '#fce7f3',
                      color: reminder.trigger_on === 'arrival' ? '#1d4ed8' : '#be185d',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: 600
                    }}>
                      {reminder.trigger_on === 'arrival' ? '📥 On Arrival' : '📤 On Departure'}
                    </span>
                    <span style={{ fontWeight: 600 }}>{reminder.name}</span>
                  </div>
                  <p style={{ margin: '4px 0', fontSize: '0.9rem' }}>{reminder.reminder_text}</p>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                    Radius: {reminder.radius_meters}m • {reminder.address}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={reminder.is_active}
                      onChange={() => handleToggleReminder(reminder.id, reminder.is_active)}
                      style={{ width: '20px', height: '20px', accentColor: '#22c55e' }}
                    />
                  </label>
                  <button
                    onClick={() => handleDeleteReminder(reminder.id)}
                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1.2rem' }}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Reminder Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '16px',
            padding: '24px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 20px', fontSize: '1.25rem', fontWeight: 700 }}>
              📍 New Location Reminder
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                Select Saved Location
              </label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                style={{
                  width: '100%', padding: '10px', borderRadius: '8px',
                  border: '1px solid #e5e7eb', fontSize: '0.9rem'
                }}
              >
                <option value="">-- Or enter custom location below --</option>
                {savedLocations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name} - {loc.address}</option>
                ))}
              </select>
            </div>

            {!selectedLocation && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                    Custom Address
                  </label>
                  <input
                    type="text"
                    value={customAddress}
                    onChange={(e) => setCustomAddress(e.target.value)}
                    placeholder="e.g., 123 Main St, City"
                    style={{
                      width: '100%', padding: '10px', borderRadius: '8px',
                      border: '1px solid #e5e7eb', fontSize: '0.9rem'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                      Latitude
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={customLat}
                      onChange={(e) => setCustomLat(e.target.value)}
                      style={{
                        width: '100%', padding: '10px', borderRadius: '8px',
                        border: '1px solid #e5e7eb', fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                      Longitude
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={customLng}
                      onChange={(e) => setCustomLng(e.target.value)}
                      style={{
                        width: '100%', padding: '10px', borderRadius: '8px',
                        border: '1px solid #e5e7eb', fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>
                <button
                  onClick={getCurrentLocation}
                  style={{
                    width: '100%', padding: '10px', marginBottom: '16px',
                    backgroundColor: '#f0f4ff', color: '#667eea',
                    border: '1px solid #667eea', borderRadius: '8px',
                    fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  📍 Use My Current Location
                </button>
              </>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                Trigger When
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <label style={{
                  flex: 1, padding: '12px', borderRadius: '8px',
                  border: `2px solid ${triggerOn === 'arrival' ? '#667eea' : '#e5e7eb'}`,
                  backgroundColor: triggerOn === 'arrival' ? '#f0f4ff' : 'white',
                  cursor: 'pointer', textAlign: 'center'
                }}>
                  <input
                    type="radio"
                    name="trigger"
                    value="arrival"
                    checked={triggerOn === 'arrival'}
                    onChange={() => setTriggerOn('arrival')}
                    style={{ display: 'none' }}
                  />
                  📥 Arriving
                </label>
                <label style={{
                  flex: 1, padding: '12px', borderRadius: '8px',
                  border: `2px solid ${triggerOn === 'departure' ? '#667eea' : '#e5e7eb'}`,
                  backgroundColor: triggerOn === 'departure' ? '#f0f4ff' : 'white',
                  cursor: 'pointer', textAlign: 'center'
                }}>
                  <input
                    type="radio"
                    name="trigger"
                    value="departure"
                    checked={triggerOn === 'departure'}
                    onChange={() => setTriggerOn('departure')}
                    style={{ display: 'none' }}
                  />
                  📤 Leaving
                </label>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                Radius (meters)
              </label>
              <input
                type="number"
                value={radiusMeters}
                onChange={(e) => setRadiusMeters(e.target.value)}
                min="50"
                max="5000"
                style={{
                  width: '100%', padding: '10px', borderRadius: '8px',
                  border: '1px solid #e5e7eb', fontSize: '0.9rem'
                }}
              />
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '4px 0 0' }}>
                How close you need to be to trigger the reminder (50-5000m)
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                Reminder Text *
              </label>
              <textarea
                value={reminderText}
                onChange={(e) => setReminderText(e.target.value)}
                placeholder="e.g., Don't forget to buy milk!"
                rows={3}
                style={{
                  width: '100%', padding: '10px', borderRadius: '8px',
                  border: '1px solid #e5e7eb', fontSize: '0.9rem', resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => { setShowAddModal(false); resetForm(); }}
                style={{
                  flex: 1, padding: '12px', backgroundColor: '#f3f4f6',
                  border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddReminder}
                style={{
                  flex: 1, padding: '12px', backgroundColor: '#667eea',
                  color: 'white', border: 'none', borderRadius: '8px',
                  fontWeight: 600, cursor: 'pointer'
                }}
              >
                Create Reminder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Location Modal */}
      {showAddLocationModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '16px',
            padding: '24px', width: '90%', maxWidth: '400px'
          }}>
            <h2 style={{ margin: '0 0 20px', fontSize: '1.25rem', fontWeight: 700 }}>
              🏠 Save New Location
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                Location Name *
              </label>
              <input
                type="text"
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
                placeholder="e.g., Home, Work, Gym"
                style={{
                  width: '100%', padding: '10px', borderRadius: '8px',
                  border: '1px solid #e5e7eb', fontSize: '0.9rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                Address *
              </label>
              <input
                type="text"
                value={newLocationAddress}
                onChange={(e) => setNewLocationAddress(e.target.value)}
                placeholder="e.g., 123 Main St, City"
                style={{
                  width: '100%', padding: '10px', borderRadius: '8px',
                  border: '1px solid #e5e7eb', fontSize: '0.9rem'
                }}
              />
            </div>

            <button
              onClick={getCurrentLocation}
              style={{
                width: '100%', padding: '12px', marginBottom: '16px',
                backgroundColor: currentLocation ? '#dcfce7' : '#f0f4ff',
                color: currentLocation ? '#166534' : '#667eea',
                border: `1px solid ${currentLocation ? '#22c55e' : '#667eea'}`,
                borderRadius: '8px', fontWeight: 600, cursor: 'pointer'
              }}
            >
              {currentLocation 
                ? `✓ Location captured (${currentLocation.lat.toFixed(4)}, ${currentLocation.lng.toFixed(4)})`
                : '📍 Get My Current Location'}
            </button>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => { setShowAddLocationModal(false); setNewLocationName(''); setNewLocationAddress(''); }}
                style={{
                  flex: 1, padding: '12px', backgroundColor: '#f3f4f6',
                  border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddLocation}
                disabled={!currentLocation}
                style={{
                  flex: 1, padding: '12px',
                  backgroundColor: currentLocation ? '#667eea' : '#d1d5db',
                  color: 'white', border: 'none', borderRadius: '8px',
                  fontWeight: 600, cursor: currentLocation ? 'pointer' : 'not-allowed'
                }}
              >
                Save Location
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationReminders;
